/* =========================================================================
   Daily streak — the home card, the full sheet, and the milestone moment
   (PLAN-design-round-2026-08-21.md §B).

   A PRESENTATION layer only. The streak itself is computed server-side on
   /game/complete + /game/round-complete (GameEndpoints.ComputeStreak: same
   ET day = hold, yesterday = +1, else reset to 1) and read back through
   /api/profile/me → stats { currentStreak, bestStreak, lastPlayedDate }.
   There is no per-day history table, so the week row is DERIVED: the run of
   `currentStreak` days ending on lastPlayedDate, shown against the calendar
   week (Mon..Sun) that contains today. "Today" is the app's liturgical day —
   America/New_York — and is passed in as a 'YYYY-MM-DD' string so every
   helper here is pure and testable with no clock and no timezone.

   Leaf module: imports only momentOverlay (chrome) and engine.esc. pathHome.ts
   wires it to the home DOM; gameController (not ours) may call
   showStreakMilestone() after /game/complete resolves.
   ========================================================================= */
import { esc } from '../engine';
import { openMoment } from './momentOverlay';

/* ------------------------------- pure logic ------------------------------- */

/** What the card/sheet need. Dates are ET-day strings 'YYYY-MM-DD'. */
export interface StreakData {
  currentStreak: number;
  bestStreak: number;
  /** Last ET day a session completed, or null for a never-played account. */
  lastPlayedDate: string | null;
  /** The ET day right now (today.ts:etDateString()). */
  today: string;
}

export type StreakStatus = 'playedToday' | 'atRisk' | 'none';

export interface WeekDot {
  /** Mon Tue Wed Thu Fri Sat Sun */
  label: string;
  /** 'YYYY-MM-DD' of this column. */
  date: string;
  /** Part of the live streak run. */
  lit: boolean;
  isToday: boolean;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const STREAK_MILESTONES = [3, 7, 14, 30] as const;

function parseDay(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? t : null;
}

function fmtDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' ± n days (pure calendar arithmetic, no timezone). */
export function addDays(iso: string, n: number): string {
  const t = parseDay(iso);
  if (t == null) return iso;
  return fmtDay(t + n * 86400000);
}

/** Signed whole days from a → b (b − a). NaN if either is malformed. */
function daysBetween(a: string, b: string): number {
  const ta = parseDay(a);
  const tb = parseDay(b);
  if (ta == null || tb == null) return NaN;
  return Math.round((tb - ta) / 86400000);
}

/** Is the streak alive, and has today's round been played? PURE. */
export function streakStatus(currentStreak: number, lastPlayedDate: string | null, today: string): StreakStatus {
  if (!lastPlayedDate || !(currentStreak > 0)) return 'none';
  const gap = daysBetween(lastPlayedDate, today);
  if (Number.isNaN(gap)) return 'none';
  if (gap <= 0) return 'playedToday'; // 0 = today; <0 = clock skew → treat as played (server does too)
  if (gap === 1) return 'atRisk';
  return 'none'; // broken — the server will reset to 1 on the next completion
}

/** The Mon..Sun week containing `today`, with the live run lit. PURE. */
export function weekDots(currentStreak: number, lastPlayedDate: string | null, today: string): WeekDot[] {
  const t = parseDay(today);
  if (t == null) return [];
  const dow = (new Date(t).getUTCDay() + 6) % 7; // Mon=0 .. Sun=6
  const monday = fmtDay(t - dow * 86400000);
  const alive = streakStatus(currentStreak, lastPlayedDate, today) !== 'none';
  // The run covers [lastPlayed − (n−1), lastPlayed].
  const runEnd = alive && lastPlayedDate ? lastPlayedDate.slice(0, 10) : null;
  const runStart = runEnd ? addDays(runEnd, -(Math.max(1, Math.floor(currentStreak)) - 1)) : null;
  const out: WeekDot[] = [];
  for (let i = 0; i < 7; i++) {
    const date = addDays(monday, i);
    const lit = !!runStart && !!runEnd && date >= runStart && date <= runEnd;
    out.push({ label: DAY_LABELS[i], date, lit, isToday: date === today });
  }
  return out;
}

/** The milestone crossed going prev → next (highest one if several), else null. */
export function milestoneFor(prev: number, next: number): 3 | 7 | 14 | 30 | null {
  let hit: 3 | 7 | 14 | 30 | null = null;
  for (const m of STREAK_MILESTONES) if (prev < m && next >= m) hit = m;
  return hit;
}

/* ------------------------------- copy ------------------------------- */

export const STREAK_RULE = 'Finish one round a day to keep it. Miss a day and it resets.';

/** The one line under the number. */
export function streakLine(d: StreakData): string {
  const s = streakStatus(d.currentStreak, d.lastPlayedDate, d.today);
  if (s === 'playedToday') return d.currentStreak === 1 ? 'Day one. Come back tomorrow to make it two.' : "Played today. You're safe. See you tomorrow.";
  if (s === 'atRisk') return `Play one round today to keep your ${d.currentStreak}-day streak.`;
  if (d.currentStreak > 0 && d.lastPlayedDate) return 'Your streak ended. Play a round today to start a new one.';
  return 'Play a round today to start a streak.';
}

/** What the server paid for a milestone (api.ts StreakMilestoneReward; db/050). */
export interface StreakReward {
  days: number;
  coinsCents: number;
  itemType: string | null;
  xpBoost: boolean;
  collectibleKey: string | null;
}

function dollars(cents: number): string {
  const d = Math.floor(Math.max(0, cents) / 100);
  return '$' + d.toLocaleString('en-US');
}

/**
 * The reward line under the milestone copy — or '' when nothing was paid
 * (older API, replay, or the reward already went out for this streak). PURE.
 *   3 → "+$150 added to your bank"     7 → "Streak Shield, one missed day forgiven"
 *  14 → "XP ×1.5 tomorrow"            30 → "Medallion + $1,000"
 * Plain text (it is escaped on render), so no icon markup here.
 */
export function rewardLine(reward: StreakReward | null | undefined): string {
  if (!reward) return '';
  const parts: string[] = [];
  if (reward.collectibleKey) parts.push('Medallion');
  if (reward.coinsCents > 0) parts.push(reward.collectibleKey ? dollars(reward.coinsCents) : `+${dollars(reward.coinsCents)} added to your bank`);
  if (reward.itemType === 'streak_shield') parts.push('Streak Shield, one missed day forgiven');
  else if (reward.itemType) parts.push(reward.itemType.replace(/_/g, ' '));
  if (reward.xpBoost) parts.push('XP ×1.5 tomorrow');
  return parts.join(reward.collectibleKey && reward.coinsCents > 0 ? ' + ' : ' · ');
}

export function milestoneLine(n: number): string {
  if (n >= 30) return 'Thirty days of faith, one round at a time. That is devotion.';
  if (n >= 14) return 'Two weeks strong. The habit is yours now.';
  if (n >= 7) return 'A full week of faith. Keep the lamp lit.';
  return 'Three days running. A habit is forming.';
}

/* ------------------------------- markup ------------------------------- */

/** Inline flame, gold → ember gradient (the brand's "lit" glyph).
    No longer on the home card (flat week strip now) — still the hero of the
    sheet and the milestone moment, and exported for other callers. */
export function flameSVG(cls = ''): string {
  return (
    `<svg class="strk-flame ${cls}" viewBox="0 0 64 80" aria-hidden="true" focusable="false">` +
    `<defs><linearGradient id="strkFlameG" x1="0" y1="0" x2="0" y2="1">` +
    `<stop offset="0" stop-color="#FFE9AC"/><stop offset="0.42" stop-color="#FFC645"/><stop offset="1" stop-color="#FF5E2A"/>` +
    `</linearGradient></defs>` +
    `<path d="M33 2c1 13 12 19 16 31 5 15-4 31-17 31S11 48 16 33c2-6 6-10 11-13-1 6 1 11 5 13 3-11-3-21 1-31Z" fill="url(#strkFlameG)"/>` +
    `<path d="M32 44c5 4 8 9 7 14-1 5-4 7-7 7s-6-2-7-7c-1-5 2-10 7-14Z" fill="#FFF4D1" opacity="0.92"/>` +
    `</svg>`
  );
}

function dotsHTML(d: StreakData, big: boolean): string {
  const dots = weekDots(d.currentStreak, d.lastPlayedDate, d.today);
  return (
    `<div class="strk-week${big ? ' strk-week-lg' : ''}" role="img" aria-label="${esc(weekAria(dots))}">` +
    dots
      .map(
        (w) =>
          `<span class="strk-day${w.lit ? ' lit' : ''}${w.isToday ? ' today' : ''}">` +
          `<span class="strk-dot" aria-hidden="true">${w.lit ? '✓' : ''}</span>` +
          `<span class="strk-lbl" aria-hidden="true">${w.label}</span>` +
          `</span>`,
      )
      .join('') +
    `</div>`
  );
}

function weekAria(dots: WeekDot[]): string {
  const lit = dots.filter((w) => w.lit).length;
  return `${lit} of 7 days played this week`;
}

function headline(d: StreakData): string {
  const s = streakStatus(d.currentStreak, d.lastPlayedDate, d.today);
  if (s === 'none') return d.currentStreak > 0 && d.lastPlayedDate ? 'Streak ended' : 'No streak yet';
  return `${d.currentStreak}-day streak`;
}

/**
 * The home card. Hidden (and emptied) when there is nothing to show — no
 * stats, or a signed-in player who has never completed a session. The host
 * is a <button>; pathHome.ts wires its click to openStreakSheet.
 */
export function renderStreakCard(host: HTMLElement | null, data: StreakData | null): void {
  if (!host) return;
  if (!data || !(data.currentStreak > 0) || !data.lastPlayedDate) {
    host.classList.add('hidden');
    host.innerHTML = '';
    return;
  }
  const s = streakStatus(data.currentStreak, data.lastPlayedDate, data.today);
  host.classList.remove('hidden');
  host.classList.toggle('strk-risk', s === 'atRisk');
  host.classList.toggle('strk-ended', s === 'none');
  host.innerHTML =
    `<span class="strk-card-head">` +
    `<span class="strk-card-title">${esc(headline(data))}</span>` +
    `<span class="strk-card-line">${esc(streakLine(data))}</span>` +
    `</span>` +
    dotsHTML(data, false);
  host.setAttribute('aria-label', `${headline(data)}. ${streakLine(data)}`);
}

/** The full sheet — flame, number, week row, rule, best. Resolves on dismiss. */
export function openStreakSheet(data: StreakData): Promise<void> {
  const s = streakStatus(data.currentStreak, data.lastPlayedDate, data.today);
  const n = Math.max(0, Math.floor(data.currentStreak));
  const moment = openMoment({
    id: 'streakSheet',
    cls: 'xp-ov strk-ov',
    label: 'Daily streak',
    html:
      `<div class="mo-card strk-card" id="streakSheetCard">` +
      `<div class="strk-hero">${flameSVG('strk-flame-lg' + (s === 'none' ? ' dim' : ''))}</div>` +
      `<div class="strk-num">${s === 'none' ? esc(headline(data)) : `${esc(n)}<span class="strk-num-unit">-day streak</span>`}</div>` +
      `<div class="strk-line${s === 'atRisk' ? ' strk-nudge' : ''}">${esc(streakLine(data))}</div>` +
      dotsHTML(data, true) +
      `<div class="strk-rule">${esc(STREAK_RULE)}</div>` +
      `<div class="strk-best">Best: <b>${esc(Math.max(data.bestStreak, n))}</b> ${Math.max(data.bestStreak, n) === 1 ? 'day' : 'days'}</div>` +
      `<button class="btn strk-cta" id="streakSheetClose" type="button">${s === 'atRisk' ? 'Play now' : 'Done'}</button>` +
      `</div>`,
  });
  // Taps inside the card must not dismiss (the dim does; so does the button).
  moment.root.querySelector<HTMLElement>('#streakSheetCard')?.addEventListener('click', (e) => e.stopPropagation());
  moment.root.querySelector<HTMLElement>('#streakSheetClose')?.addEventListener('click', () => {
    moment.close();
    if (s === 'atRisk') {
      // "Play now" — hand off to the one primary launch path (home's #startBtn).
      const start = document.getElementById('startBtn');
      if (start && typeof (start as HTMLButtonElement).click === 'function') (start as HTMLButtonElement).click();
    }
  });
  return moment.done;
}

/**
 * The milestone moment — 3 / 7 / 14 / 30 days. Same chrome as the round-end
 * moments. Call AFTER /game/complete resolves with
 *   milestoneFor(prevStreak, resp.currentStreak)
 * and only when it is non-null. Resolves when the player continues.
 * `reward` (resp.streakMilestone, db/050) adds the "+$150 added to your bank" /
 * shield / XP ×1.5 / medallion line; omit it and the moment is copy only.
 */
export function showStreakMilestone(n: number, reward?: StreakReward | null): Promise<void> {
  const days = Math.max(1, Math.floor(n));
  const rl = rewardLine(reward);
  const moment = openMoment({
    id: 'streakMilestone',
    cls: 'xp-ov strk-ov strk-ms-ov',
    label: `${days}-day streak`,
    guardMs: 500,
    autoMs: 9000,
    html:
      `<div class="mo-card strk-card strk-ms-card">` +
      `<div class="strk-kicker">Streak milestone</div>` +
      `<div class="strk-hero">${flameSVG('strk-flame-lg strk-flame-pop')}</div>` +
      `<div class="strk-num">${esc(days)}<span class="strk-num-unit">-day streak</span></div>` +
      `<div class="strk-line">${esc(milestoneLine(days))}</div>` +
      (rl ? `<div class="strk-reward" role="status">${esc(rl)}</div>` : '') +
      `<button class="btn strk-cta" id="streakMilestoneContinue" type="button">Continue</button>` +
      `</div>`,
  });
  moment.root.querySelector<HTMLElement>('#streakMilestoneContinue')?.addEventListener('click', () => moment.close());
  return moment.done;
}
